import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../axios/axiosInstance";
import { handeleUpdateStatus } from "../../../utils/update-request-status";

interface Prop {
  onClose: () => void;
  requestData?: any;
  requestId?: any;
}

const ZoneForm: React.FC<Prop> = ({ onClose, requestData, requestId }) => {
  const [formData, setFormData] = useState<any>({
    zone_name: "",
    area: "",
    leader: null,
  });
  const [members, setMembers] = useState<any[]>([]);
  const [existingZone, setExistingZone] = useState<any>(null);

  useEffect(() => {
    fetchMembers();
    if (requestData) {
      setFormData({
        zone_name: requestData?.name || requestData?.zone_name || "",
        area: requestData?.area || requestData?.zone_area || "",
        leader: requestData?.leader || null,
      });
      
      // If updating, fetch existing zone data
      if (requestData?.id) {
        fetchZone(requestData.id);
      }
    }
  }, [requestData]);

  const fetchMembers = async () => {
    try {
      const res = await axiosInstance.get<{ data: any[] }>(`/api/zone/members`);
      setMembers(res.data?.data || []);
    } catch (err) {
      toast.error("Failed to fetch members");
    }
  };

  const fetchZone = async (id: number) => {
    try {
      const res = await axiosInstance.get<{ data: any }>(`/api/zone/${id}`);
      if (res.status === 200) {
        setExistingZone(res.data?.data);
      }
    } catch (err) {
      console.error("Error fetching zone:", err);
    }
  };

  const handleFormChange = (key: string, value: any) => {
    setFormData({ ...formData, [key]: value });
  };

  const handleSubmit = async () => {
    try {
      if (!formData?.zone_name) {
        toast.error("Zone name is required");
        return;
      }

      const payload = {
        name: formData.zone_name,
        area: formData.area || "",
        leader: formData.leader || null,
      };

      // When approving, execute directly without invocation
      if (requestData?.id) {
        // UPDATE
        const response = await axiosInstance.put(
          `/api/zone/${requestData.id}`,
          payload
        );
        if (response.status === 200) {
          if (requestId) {
            handeleUpdateStatus(requestId, "1");
          }
          toast.success("Request approved and zone updated successfully");
          onClose();
        }
      } else {
        // CREATE
        const response = await axiosInstance.post(`/api/zone`, payload);
        if (response.status === 200) {
          if (requestId) {
            handeleUpdateStatus(requestId, "1");
          }
          toast.success("Request approved and zone created successfully");
          onClose();
        }
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.body || err?.message || "Error saving zone");
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {requestData?.id ? "Approve Zone Update" : "Approve Zone Creation"}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Review the zone details below and make any necessary changes before approving.
        </p>
      </div>

      {/* Show existing data if updating */}
      {existingZone && requestData?.id && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <h3 className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
            Current Zone Details
          </h3>
          <div className="space-y-1 text-sm text-yellow-700 dark:text-yellow-400">
            <p><strong>Name:</strong> {existingZone.zone_name}</p>
            <p><strong>Area:</strong> {existingZone.zone_area}</p>
            {existingZone.leader && (
              <p><strong>Leader:</strong> {existingZone.leader.name}</p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Zone Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className="dark-input w-full px-4 py-2.5"
            value={formData?.zone_name || ""}
            onChange={(e: any) => handleFormChange("zone_name", e.target.value)}
            maxLength={25}
          />
          {formData?.zone_name?.length > 25 && (
            <p className="text-red-500 text-xs mt-1">
              Character limit exceeded (25 max)
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Area
          </label>
          <small className="text-gray-600 dark:text-gray-300 block mb-2">
            Eg, Area 1, Area 2, Area 3
          </small>
          <input
            type="text"
            className="dark-input w-full px-4 py-2.5"
            value={formData?.area || ""}
            onChange={(e: any) => handleFormChange("area", e.target.value)}
            maxLength={250}
          />
          {formData?.area?.length > 250 && (
            <p className="text-red-500 text-xs mt-1">
              Character limit exceeded (250 max)
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Zone Leader
          </label>
          <select
            className="dark-input w-full px-4 py-2.5"
            value={formData?.leader || ""}
            onChange={(e: any) => handleFormChange("leader", e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select a leader</option>
            {members.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name} {member.phone ? `(${member.phone})` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="btn-primary px-6 py-2.5"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn-primary px-6 py-2.5 bg-green-600 hover:bg-green-700"
          >
            Approve & {requestData?.id ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ZoneForm;
